import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Lock, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, formatPrice, cartTotalInr, currency, user, refreshUser } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount_inr, desc}
  const [autoApplied, setAutoApplied] = useState(false);
  const couponDiscount = coupon?.discount_inr || 0;
  const walletBalance = user?.wallet_balance || 0;
  const afterCoupon = Math.max(0, cartTotalInr - couponDiscount);
  const walletApplicable = Math.min(walletBalance, afterCoupon);
  const walletUsed = useWallet ? walletApplicable : 0;
  const payableInr = Math.max(0, afterCoupon - walletUsed);
  const pointsToEarn = Math.round(cartTotalInr * 0.05);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput, amount_inr: cartTotalInr });
      setCoupon(data);
      toast.success(`Coupon applied — ${data.desc}`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid coupon");
    }
  };

  // Auto-apply an eligible coupon (e.g. first-order WELCOME10) for logged-in shoppers
  useEffect(() => {
    if (!user || coupon || autoApplied || cartTotalInr <= 0) return;
    api.get(`/coupons/auto?amount_inr=${cartTotalInr}`).then(({ data }) => {
      if (data.coupon) {
        setCoupon(data.coupon);
        setCouponInput(data.coupon.code);
        setAutoApplied(true);
        toast.success(`🎁 ${data.coupon.code} auto-applied — ${data.coupon.desc}`);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const [shipping, setShipping] = useState({
    name: user?.name || "",
    phone: "",
    address: "",
    city: "",
    region: "",
    postal: "",
    country: "India",
  });

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="font-display text-2xl font-bold mb-3">Your bag is empty</h2>
        <Button onClick={() => navigate("/shop")} className="bg-[#E63946] hover:bg-[#d12836]">Shop Now</Button>
      </div>
    );
  }

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const items = cart.map((i) => ({
        product_id: i.product.id,
        quantity: i.quantity,
        size: i.size,
        color: i.color,
        logo: i.logo !== false,
      }));

      const { data: cfg } = await api.get("/payments/config");

      if (cfg.provider === "razorpay") {
        const { data } = await api.post("/payments/razorpay/create-order", {
          items, currency, origin_url: window.location.origin, shipping, use_wallet: walletUsed, coupon_code: coupon?.code || null,
        });
        if (data.fully_paid) {
          window.location.href = `/payment/success?session_id=${data.session_id}`;
          return;
        }
        await loadRazorpayScript();
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.razorpay_order_id,
          name: "Bull Creations",
          description: "Order payment",
          theme: { color: "#E63946" },
          prefill: { name: shipping.name || data.name, email: data.email, contact: shipping.phone },
          handler: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                internal_order_id: data.internal_order_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              window.location.href = `/payment/success?session_id=${data.session_id}`;
            } catch {
              toast.error("Payment verification failed");
              setLoading(false);
            }
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
        return;
      }

      // Stripe (default)
      const { data } = await api.post("/payments/checkout", {
        items,
        currency,
        origin_url: window.location.origin,
        shipping,
        use_wallet: walletUsed,
        coupon_code: coupon?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-2xl sm:text-3xl font-bold mb-8">Checkout</h1>
      <form onSubmit={pay} className="grid lg:grid-cols-12 gap-8">
        {/* shipping */}
        <div className="lg:col-span-7 space-y-5">
          <div className="p-6 rounded-2xl border bg-white dark:bg-slate-900">
            <h3 className="font-display font-bold mb-4">Shipping Address</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Full Name</Label>
                <Input required value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} data-testid="ship-name" className="mt-1" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input required value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} data-testid="ship-phone" className="mt-1" />
              </div>
              <div>
                <Label>Country</Label>
                <select
                  value={shipping.country}
                  onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                  data-testid="ship-country"
                  className="mt-1 w-full h-10 px-3 rounded-md border bg-white dark:bg-slate-900 text-sm"
                >
                  <option>India</option>
                  <option>UAE</option>
                  <option>Saudi Arabia</option>
                  <option>Qatar</option>
                  <option>Oman</option>
                  <option>Kuwait</option>
                  <option>Bahrain</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Input required value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} data-testid="ship-address" className="mt-1" />
              </div>
              <div>
                <Label>City</Label>
                <Input required value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} data-testid="ship-city" className="mt-1" />
              </div>
              <div>
                <Label>State / Emirate</Label>
                <Input required value={shipping.region} onChange={(e) => setShipping({ ...shipping, region: e.target.value })} data-testid="ship-region" className="mt-1" />
              </div>
              <div>
                <Label>PIN / Postal Code</Label>
                <Input required value={shipping.postal} onChange={(e) => setShipping({ ...shipping, postal: e.target.value })} data-testid="ship-postal" className="mt-1" />
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[#1D3557]/5 border border-[#1D3557]/10 flex items-center gap-3 text-sm">
            <Lock size={18} className="text-[#1D3557]" />
            Secure payment — a protected checkout window will open to complete your payment.
          </div>
        </div>

        {/* summary */}
        <div className="lg:col-span-5">
          <div className="p-6 rounded-2xl border bg-white dark:bg-slate-900 sticky top-24">
            <h3 className="font-display font-bold mb-4">Order Summary</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {cart.map((item, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <img src={item.product.images?.[0]} alt="" className="w-14 h-16 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium line-clamp-1">{item.product.name}</div>
                    <div className="text-xs text-slate-400">{item.size} • {item.color} • {item.logo ? "With logo" : "No logo"} • Qty {item.quantity}</div>
                  </div>
                  <div className="font-mono-price font-semibold">{formatPrice(item.product.price * item.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-price">{formatPrice(cartTotalInr)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="text-emerald-600 font-semibold">FREE</span></div>

              {/* coupon */}
              <div className="py-2">
                {coupon ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="coupon-applied">
                    <span className="text-sm font-semibold text-emerald-700">🎟 {coupon.code} applied</span>
                    <button type="button" onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-xs text-red-500 font-semibold" data-testid="coupon-remove">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Coupon code (e.g. WELCOME10)"
                      data-testid="coupon-input"
                      className="flex-1 h-10 px-3 rounded-lg border text-sm bg-white dark:bg-slate-900"
                    />
                    <Button type="button" variant="outline" onClick={applyCoupon} data-testid="coupon-apply">Apply</Button>
                  </div>
                )}
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-emerald-700"><span>Coupon discount</span><span className="font-mono-price">-{formatPrice(couponDiscount)}</span></div>
              )}
              {user && walletBalance > 0 && (
                <label className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 cursor-pointer" data-testid="use-wallet-toggle">
                  <span className="flex items-center gap-2 text-[#1D3557] font-medium">
                    <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} data-testid="use-wallet-checkbox" />
                    Use Bull Family wallet
                  </span>
                  <span className="font-mono-price font-semibold text-[#E63946]">-{formatPrice(walletUsed)}</span>
                </label>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>To Pay</span><span className="font-mono-price" data-testid="checkout-total">{formatPrice(payableInr)}</span></div>
              {user && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 mt-1">
                  <Sparkles size={13} /> You'll earn <strong>{pointsToEarn} points</strong> with this order
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="place-order-button"
              className="w-full mt-5 bg-[#E63946] hover:bg-[#d12836] h-12 text-base font-semibold"
            >
              <CreditCard size={18} className="mr-2" />
              {loading ? "Redirecting..." : payableInr <= 0 ? `Place Order (Wallet)` : `Pay ${formatPrice(payableInr)}`}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
